import React, { useCallback } from 'react';
import classNames from 'classnames';
import Icon from '../Icon/Icon';

export type StepItem = string | { label: string; icon: string };

interface GiftVoucherStepIndicatorProps {
	steps: StepItem[];
	currentStepIndex: number;
}

export function GiftVoucherStepIndicator({ steps, currentStepIndex }: GiftVoucherStepIndicatorProps) {
	// Count icon steps so numbered steps start at 1
	const iconStepCount = steps.filter(s => typeof s !== 'string').length;
	const activeRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (node) {
				node.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
			}
		},
		[currentStepIndex],
	);

	return (
		<div className="gift-voucher-step-indicator">
			{steps.map((step, index) => {
				const isCompleted = index < currentStepIndex;
				const isActive = index === currentStepIndex;
				const label = typeof step === 'string' ? step : step.label;
				const icon = typeof step === 'string' ? undefined : step.icon;

				const renderCircleContent = () => {
					if (isCompleted || (isActive && index === steps.length - 1)) {
						return <Icon name="check" useMaterialIcons classname="gift-voucher-step__check" />;
					}
					if (icon) {
						return <Icon name={icon} useMaterialIcons />;
					}
					return <span>{index + 1 - iconStepCount}</span>;
				};

				return (
					<React.Fragment key={label}>
						{index > 0 && (
							<div
								className={classNames('gift-voucher-step__connector', {
									'gift-voucher-step__connector--completed': index <= currentStepIndex,
								})}
							/>
						)}
						<div
							ref={isActive ? activeRef : undefined}
							className={classNames('gift-voucher-step', {
								'gift-voucher-step--completed': isCompleted,
								'gift-voucher-step--active': isActive,
								'gift-voucher-step--upcoming': !isCompleted && !isActive,
							})}
						>
							<div className="gift-voucher-step__circle">{renderCircleContent()}</div>
							<span className="gift-voucher-step__label">{label}</span>
						</div>
					</React.Fragment>
				);
			})}
		</div>
	);
}
